import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Theme } from '../../hooks/useTheme.js';

/** 対応言語 */
/** @graph-connects none */
const LANGUAGES = [
  { code: 'ja', label: 'JA' },
  { code: 'en', label: 'EN' },
] as const;

/** サービス選択肢 */
/** @graph-connects none */
const SERVICE_OPTIONS = [
  { value: '', label: 'All Services' },
  { value: 'aircloset', label: 'airCloset' },
  { value: 'styling_console', label: 'Styling Console' },
];

interface HeaderProps {
  /** ユーザー情報 */
  user: {
    name: string;
    email: string;
    picture: string;
  } | null;
  /** ログアウト処理 */
  onLogout: () => void;
  /** サイドバー開閉（モバイル用） */
  onMenuToggle: () => void;
  /** 現在のテーマ */
  theme: Theme;
  /** テーマ切替 */
  onThemeToggle: () => void;
  /** 現在のサービス（'' = 全サービス） */
  service: string;
  /** サービス変更 */
  onServiceChange: (service: string) => void;
}

/** @graph-connects none */
const THEME_ICONS: Record<Theme, React.ReactNode> = {
  light: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  ),
  dark: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
      />
    </svg>
  ),
};

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business アプリヘッダー。airCloset/Styling Consoleサービス切替・JA/EN言語切替・ダークモード・ユーザードロップダウンを配置
 * @graph-connects none
 */
export function Header({
  user,
  onLogout,
  onMenuToggle,
  theme,
  onThemeToggle,
  service,
  onServiceChange,
}: HeaderProps) {
  const { t, i18n } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  // ボタンの位置からドロップダウンの位置を計算
  const updatePos = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  // メニュー外クリックで閉じる
  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setIsUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isUserMenuOpen]);

  return (
    <header className="sticky top-0 glass-header border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between h-14 px-4 md:px-6">
        {/* 左: メニューボタン（モバイル） */}
        <button
          onClick={onMenuToggle}
          className="md:hidden p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
          aria-label="メニューを開く"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* ロゴ + サービス切替 */}
        <div className="flex items-center gap-3">
          <img src="/favicon.svg" alt="UX Mirror" className="w-6 h-6" />
          <select
            value={service}
            onChange={(e) => {
              onServiceChange(e.target.value);
            }}
            className="px-3 py-1.5 rounded-lg bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] text-xs font-medium border border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none transition-colors cursor-pointer"
          >
            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* 右: テーマ切替 + ユーザーメニュー */}
        <div className="flex items-center gap-2">
          {/* 言語切替 */}
          <div className="flex items-center rounded-lg border border-[var(--color-border)] overflow-hidden">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  void i18n.changeLanguage(lang.code);
                }}
                className={`px-2 py-1 text-xs font-medium transition-colors ${
                  i18n.language === lang.code
                    ? 'bg-[var(--color-accent-surface)] text-[var(--color-accent-primary)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>

          {/* テーマトグル */}
          <button
            onClick={onThemeToggle}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors text-sm"
            aria-label={t(`theme.${theme}`)}
            title={t(`theme.${theme}`)}
          >
            {THEME_ICONS[theme]}
            <span className="hidden sm:inline text-xs">{t(`theme.${theme}`)}</span>
          </button>

          {/* ユーザーメニュー */}
          {user && (
            <div>
              <button
                ref={buttonRef}
                onClick={() => {
                  if (!isUserMenuOpen) {
                    updatePos();
                  }
                  setIsUserMenuOpen(!isUserMenuOpen);
                }}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-7 h-7 rounded-full"
                  referrerPolicy="no-referrer"
                />
                <span className="hidden sm:block text-sm text-[var(--color-text-primary)]">
                  {user.name}
                </span>
              </button>

              {/* ドロップダウン（portalでheaderのbackdrop-filter外に描画） */}
              {isUserMenuOpen &&
                pos &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    className="glass-dropdown"
                    style={{ top: pos.top, right: pos.right }}
                  >
                    <div className="px-4 py-3 border-b border-[var(--color-border)]">
                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                        {user.name}
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] truncate">
                        {user.email}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        onLogout();
                      }}
                      className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg-hover)] transition-colors"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      {t('auth.logout', 'ログアウト')}
                    </button>
                  </div>,
                  document.body,
                )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
